import React, { useState, useRef, useEffect } from 'react';
import { useToggleReaction } from '../hooks/useMessaging';
import '../styles/MessageReactions.css';

// Available emoji reactions
const AVAILABLE_EMOJIS = [
  { emoji: '👍', label: 'Thumbs Up' },
  { emoji: '❤️', label: 'Heart' },
  { emoji: '😂', label: 'Laughing' },
  { emoji: '😮', label: 'Surprised' },
  { emoji: '😢', label: 'Sad' },
  { emoji: '😡', label: 'Angry' },
  { emoji: '🔥', label: 'Fire' },
  { emoji: '👏', label: 'Clapping' },
  { emoji: '🎉', label: 'Party' },
  { emoji: '💯', label: 'Hundred' },
];

const MessageReactions = ({ message, currentUserId, isOwnMessage = false }) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const toggleReactionMutation = useToggleReaction();
  const pickerRef = useRef(null);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleReaction = async (emoji) => {
    try {
      await toggleReactionMutation.mutateAsync({
        messageId: message.id,
        emoji
      });
      setShowEmojiPicker(false);
      
      // Force a small delay to ensure optimistic update is visible
      setTimeout(() => {
        // This will force a re-render by setting state
        setShowEmojiPicker(false);
      }, 100);
      
    } catch (error) {
      console.error('Error toggling reaction:', error);
      // Show user-friendly error message
      alert('Erreur lors de l\'ajout de la réaction. Veuillez réessayer.');
    }
  };

  const hasReactions = message.reaction_summary && Object.keys(message.reaction_summary).length > 0;
  const isOptimistic = message.optimisticReaction;

  return (
    <div className={`message-reactions ${isOwnMessage ? 'own-message' : ''} ${isOptimistic ? 'optimistic-update' : ''}`}>
      {/* Display existing reactions */}
      {hasReactions && (
        <div className="reactions-display">
          {Object.entries(message.reaction_summary).map(([emoji, count]) => (
            <button
              key={emoji}
              className={`reaction-button ${message.user_reaction === emoji ? 'user-reacted' : ''}`}
              onClick={() => handleToggleReaction(emoji)}
              title={`${count} ${count === 1 ? 'personne a' : 'personnes ont'} réagi avec ${emoji}`}
            >
              <span className="reaction-emoji">{emoji}</span>
              <span className="reaction-count">{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Add reaction button */}
      <div className="reaction-picker-container" ref={pickerRef}>
        <button
          className="add-reaction-button"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          title="Ajouter une réaction"
        >
          <span className="add-reaction-icon">😊</span>
        </button>

        {/* Emoji picker dropdown */}
        {showEmojiPicker && (
          <div className="emoji-picker-dropdown">
            <div className="emoji-picker-grid">
              {AVAILABLE_EMOJIS.map(({ emoji, label }) => (
                <button
                  key={emoji}
                  className="emoji-option"
                  onClick={() => handleToggleReaction(emoji)}
                  title={label}
                  disabled={toggleReactionMutation.isLoading}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageReactions;
